/**
 * royaleTopics.ts
 *
 * The curated topic list for Puzzle Royale's host settings. Each entry maps a
 * `cc_puzzles_global` per-theme BOOLEAN COLUMN (value 1 = puzzle has theme) to
 * a human label. The host multi-selects topics; the selected column keys are
 * sent as `topics` and OR-matched server-side (a puzzle qualifies if ANY
 * selected column = 1) — see chess/fun_challenges/views.py _puzzle_sequence_impl.
 *
 * IMPORTANT: difficulty is handled SEPARATELY via player-move-count (NOT the
 * matein* columns), so mate-specific theme columns are deliberately excluded
 * here — difficulty already covers "how many moves to mate". These keys must
 * match real column names on cc_puzzles_global and the server-side allowlist.
 */

export interface RoyaleTopic {
  /** cc_puzzles_global boolean column name. */
  key: string;
  /** Display label in the dropdown. */
  label: string;
}

/**
 * Tactical-motif topics. Curated from the cc_puzzles_global schema (the
 * boolean theme columns), excluding mate-length columns (matein1..5) which
 * difficulty owns, and excluding pure phase/quality columns (opening,
 * middlegame, endgame, master, etc.) which aren't motifs a learner picks.
 */
export const ROYALE_TOPICS: RoyaleTopic[] = [
  {key: 'fork', label: 'Fork'},
  {key: 'pin', label: 'Pin'},
  {key: 'skewer', label: 'Skewer'},
  {key: 'discoveredattack', label: 'Discovered Attack'},
  {key: 'doublecheck', label: 'Double Check'},
  {key: 'sacrifice', label: 'Sacrifice'},
  {key: 'deflection', label: 'Deflection'},
  {key: 'attraction', label: 'Attraction'},
  {key: 'interference', label: 'Interference'},
  {key: 'clearance', label: 'Clearance'},
  {key: 'intermezzo', label: 'Zwischenzug (In-between)'},
  {key: 'xrayattack', label: 'X-Ray Attack'},
  {key: 'hangingpiece', label: 'Hanging Piece'},
  {key: 'trappedpiece', label: 'Trapped Piece'},
  {key: 'capturingdefender', label: 'Capturing the Defender'},
  {key: 'quietmove', label: 'Quiet Move'},
  {key: 'defensivemove', label: 'Defensive Move'},
  {key: 'advancedpawn', label: 'Advanced Pawn'},
  {key: 'promotion', label: 'Promotion'},
  {key: 'underpromotion', label: 'Underpromotion'},
  {key: 'enpassant', label: 'En Passant'},
  {key: 'castling', label: 'Castling'},
  {key: 'exposedking', label: 'Exposed King'},
  {key: 'kingsideattack', label: 'Kingside Attack'},
  {key: 'queensideattack', label: 'Queenside Attack'},
  {key: 'attackingf2f7', label: 'Attacking f2/f7'},
  {key: 'backrankmate', label: 'Back-Rank'},
  {key: 'zugzwang', label: 'Zugzwang'},
  {key: 'crushing', label: 'Crushing'},
  {key: 'advantage', label: 'Advantage'},
  {key: 'equality', label: 'Equality'},
];

/** Quick lookup: column key → label. */
export const TOPIC_LABELS: Record<string, string> = ROYALE_TOPICS.reduce(
  (acc, t) => {
    acc[t.key] = t.label;
    return acc;
  },
  {} as Record<string, string>,
);

/** CCSelect items shape. */
export const TOPIC_SELECT_ITEMS = ROYALE_TOPICS.map(t => ({
  label: t.label,
  value: t.key,
}));
